/**
 * WebSocket client with auto-reconnect, exponential backoff, and event subscription.
 *
 * Auth: relies on the same HttpOnly JWT cookie as the REST API. The browser
 * sends cookies automatically on the upgrade handshake.
 */

export type WSEvent = {
	type: string;
	account_id?: string;
	data?: unknown;
};

export type WSStatus = "idle" | "connecting" | "open" | "closed" | "error";

type Listener = (evt: WSEvent) => void;
type StatusListener = (status: WSStatus) => void;

const WS_URL = (() => {
	// En modo desarrollo, siempre pasamos por el proxy de Vite en localhost
	// para que comparta el origen, incluya cookies de sesión y haga ws->wss sin error de certificado
	if (import.meta.env.DEV) {
		const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
		return `${proto}//${window.location.host}/api/v1/ws`;
	}

	// En producción, usamos la URL configurada
	const base = import.meta.env.VITE_WS_URL || import.meta.env.VITE_API_BASE_URL;
	if (base) {
		const isHttps = base.startsWith("https://") || window.location.protocol === "https:";
		const proto = isHttps ? "wss:" : "ws:";
		const host = base.replace(/^https?:\/\//, "").replace(/\/+$/, "");
		// Evitar duplicar /api/v1 si ya viniese en base
		const cleanHost = host.replace(/\/api\/v1$/, "");
		return `${proto}//${cleanHost}/api/v1/ws`;
	}

	// Fallback de mismo origen
	const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
	return `${proto}//${window.location.host}/api/v1/ws`;
})();

class WSClient {
	private ws: WebSocket | null = null;
	private listeners = new Set<Listener>();
	private statusListeners = new Set<StatusListener>();
	private status: WSStatus = "idle";
	private reconnectAttempts = 0;
	private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
	private shouldReconnect = false;
	private accountId: string | null = null;
	// Optional guard — if it returns false the reconnect is aborted.
	// WebSocketProvider sets this to () => !!authStore.user so reconnects
	// never fire when the session has expired.
	private authGuard: (() => boolean) | null = null;

	setAuthGuard(guard: () => boolean) {
		this.authGuard = guard;
	}

	connect(accountId?: string | null) {
		this.shouldReconnect = true;
		this.accountId = accountId ?? null;

		if (
			this.ws &&
			(this.ws.readyState === WebSocket.OPEN ||
				this.ws.readyState === WebSocket.CONNECTING)
		) {
			return;
		}

		this.setStatus("connecting");

		const url = this.accountId
			? `${WS_URL}?account_id=${encodeURIComponent(this.accountId)}`
			: WS_URL;
		try {
			this.ws = new WebSocket(url);
		} catch {
			this.setStatus("error");
			this.scheduleReconnect();
			return;
		}

		this.ws.onopen = () => {
			this.reconnectAttempts = 0;
			this.setStatus("open");
		};

		this.ws.onmessage = (e) => {
			try {
				const evt = JSON.parse(e.data) as WSEvent;
				this.listeners.forEach((l) => {
					l(evt);
				});
			} catch {
				// ignore non-JSON
			}
		};

		this.ws.onerror = () => {
			this.setStatus("error");
		};

		this.ws.onclose = (e) => {
			this.setStatus("closed");
			this.ws = null;
			// Codes 4000-4999 are app-level errors (4001 = Unauthorized).
			// Don't retry — waiting won't fix an auth problem.
			const isAppError = e.code >= 4000;
			if (this.shouldReconnect && !isAppError) {
				this.scheduleReconnect();
			}
		};
	}

	disconnect() {
		this.shouldReconnect = false;
		if (this.reconnectTimer) {
			clearTimeout(this.reconnectTimer);
			this.reconnectTimer = null;
		}
		if (this.ws) {
			this.ws.close();
			this.ws = null;
		}
		this.setStatus("idle");
	}

	on(listener: Listener): () => void {
		this.listeners.add(listener);
		return () => {
			this.listeners.delete(listener);
		};
	}

	onStatus(listener: StatusListener): () => void {
		this.statusListeners.add(listener);
		listener(this.status);
		return () => {
			this.statusListeners.delete(listener);
		};
	}

	getStatus() {
		return this.status;
	}

	private setStatus(s: WSStatus) {
		if (this.status === s) return;
		this.status = s;
		this.statusListeners.forEach((l) => {
			l(s);
		});
	}

	private scheduleReconnect() {
		if (!this.shouldReconnect || this.reconnectTimer) return;
		this.reconnectAttempts += 1;
		// Exponential backoff capped at 30s
		const delay = Math.min(
			1000 * 2 ** Math.min(this.reconnectAttempts, 5),
			30000,
		);
		this.reconnectTimer = setTimeout(() => {
			this.reconnectTimer = null;
			// Abort if auth guard says the user is no longer authenticated.
			if (this.authGuard && !this.authGuard()) return;
			this.connect(this.accountId);
		}, delay);
	}
}

export const wsClient = new WSClient();
